import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  X,
  Send,
  Loader2,
  Sparkles,
  HelpCircle,
  Lightbulb,
  ClipboardList,
  Mic,
  Baby,
  Users,
} from "lucide-react";
import MicButton from "@/components/accessibility/MicButton";
import ReadAloudButton from "@/components/accessibility/ReadAloudButton";
import LemonBotHelpButtons from "@/components/accessibility/LemonBotHelpButtons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEMONBOT_AVATAR =
  "https://customer-assets.emergentagent.com/job_7adefe5a-30ab-4416-ba08-2d19308841cd/artifacts/r3wtiuxz_Untitled-2_20251207_103450.png";

// Six canonical quick actions — labels render on chips, prompts get sent.
const QUICK_ACTIONS = [
  {
    intent: "understand_lesson",
    label: "Help me understand this lesson",
    short: "Understand",
    icon: HelpCircle,
    prompt: "Help me understand this lesson in simple words.",
  },
  {
    intent: "give_example",
    label: "Give me an example",
    short: "Example",
    icon: Lightbulb,
    prompt: "Give me a real-life example for this lesson.",
  },
  {
    intent: "business_plan_help",
    label: "Help me with my business plan",
    short: "Plan",
    icon: ClipboardList,
    prompt: "Help me with my business plan based on what I have so far.",
  },
  {
    intent: "pitch_practice",
    label: "Help me practice my pitch",
    short: "Pitch",
    icon: Mic,
    prompt:
      "Help me practice my pitch — give me a 30-second template I can fill in, then react to my answer.",
  },
  {
    intent: "explain_easier",
    label: "Explain this easier",
    short: "Easier",
    icon: Baby,
    prompt: "Explain this lesson easier, like I'm in 6th grade.",
  },
  {
    intent: "team_next_step",
    label: "What should my team do next?",
    short: "Team",
    icon: Users,
    prompt:
      "What should my team do next? Use DISC Birds (Eagle/Parrot/Dove/Owl) to assign roles.",
  },
];

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

/**
 * Floating, lesson-aware LemonBot coach.
 *
 * Props (all optional):
 *   lessonId, moduleId, lessonTitle, moduleTitle, activity
 *
 * Pass whatever context the host page knows. The backend resolves missing
 * fields from `lesson_id` when needed, so on a lesson page passing just
 * `lessonId` is enough.
 */
function LemonBotFloater({
  lessonId,
  moduleId,
  lessonTitle,
  moduleTitle,
  activity = "general",
}) {
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState(true); // default-on, hide if API says off
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hey, I'm LemonBot 🍋 — your AI Business Coach. Tap a quick action below or ask me anything about your lesson, your business idea, or your pitch!",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [intentInFlight, setIntentInFlight] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Resolve availability (admin global kill-switch + per-class toggle).
  // We hide the floater entirely when disabled — students don't even see it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/lemonbot/availability`, authHeaders());
        if (!cancelled) setAvailable(!!res.data?.enabled);
      } catch {
        // On error, fail open (don't punish students for a flaky API call)
        if (!cancelled) setAvailable(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      // Small delay so the panel transition completes
      const t = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Esc closes the panel
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const sendMessage = useCallback(
    async (text, intent = "freeform") => {
      const trimmed = (text || "").trim();
      if (!trimmed || loading) return;

      setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
      setLoading(true);
      setIntentInFlight(intent);
      setInput("");

      try {
        const res = await axios.post(
          `${API}/lemonbot/chat`,
          {
            message: trimmed,
            intent,
            lesson_id: lessonId,
            module_id: moduleId,
            lesson_title: lessonTitle,
            module_title: moduleTitle,
            activity,
            language:
              localStorage.getItem("i18nextLng") ||
              (typeof navigator !== "undefined" && navigator.language) ||
              "en",
          },
          authHeaders()
        );
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: res.data.response },
        ]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "I'm having a brain freeze right now 🍋 — try me again in a moment!",
          },
        ]);
      } finally {
        setLoading(false);
        setIntentInFlight(null);
      }
    },
    [loading, lessonId, moduleId, lessonTitle, moduleTitle, activity]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input, "freeform");
  };

  const handleQuickAction = (action) => {
    sendMessage(action.prompt, action.intent);
  };

  return (
    <>
      {!available && null}
      {/* Floating launcher button (always visible when available) */}
      {available && !open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="lemonbot-floater-open-btn"
          aria-label="Open LemonBot AI Coach"
          className="fixed bottom-5 right-5 z-40 group flex items-center gap-2 bg-gradient-to-br from-yellow-400 via-amber-400 to-orange-400 hover:from-yellow-500 hover:via-amber-500 hover:to-orange-500 text-white rounded-full shadow-2xl pl-2 pr-4 py-2 transition-all hover:scale-105"
        >
          <span className="relative flex">
            <span className="absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-60 animate-ping" />
            <img
              src={LEMONBOT_AVATAR}
              alt=""
              className="relative w-10 h-10 rounded-full border-2 border-white"
            />
          </span>
          <span className="font-bold hidden sm:inline">Ask LemonBot</span>
          <span className="font-bold sm:hidden">LemonBot</span>
        </button>
      )}

      {/* Chat panel */}
      {available && open && (
        <div
          className="fixed z-50 inset-x-0 bottom-0 sm:bottom-5 sm:right-5 sm:left-auto sm:w-[400px] sm:max-w-[calc(100vw-2.5rem)] flex flex-col bg-white sm:rounded-2xl shadow-2xl border border-yellow-200 overflow-hidden"
          style={{ height: "min(85vh, 640px)" }}
          data-testid="lemonbot-floater-panel"
          role="dialog"
          aria-label="LemonBot AI Coach"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <img
              src={LEMONBOT_AVATAR}
              alt=""
              className="w-10 h-10 rounded-full border-2 border-white shadow"
            />
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold leading-tight flex items-center gap-1.5">
                LemonBot
                <Sparkles className="w-4 h-4 text-yellow-100" />
              </div>
              <div
                className="text-[11px] text-white/85 truncate"
                title={lessonTitle || moduleTitle || ""}
              >
                {lessonTitle
                  ? `On: ${lessonTitle}`
                  : moduleTitle
                  ? `Module: ${moduleTitle}`
                  : "Your AI Business Coach"}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="lemonbot-floater-close-btn"
              aria-label="Close LemonBot"
              className="text-white/90 hover:text-white p-1 rounded-full hover:bg-white/15 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-yellow-50/40 to-white"
            data-testid="lemonbot-floater-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-yellow-400 text-gray-900 rounded-br-sm"
                      : "bg-white border border-yellow-200 text-gray-800 rounded-bl-sm shadow-sm"
                  }`}
                  data-testid={`lemonbot-msg-${i}-${m.role}`}
                >
                  {m.content}
                  {m.role === "assistant" && m.content && (
                    <div className="mt-1 -mb-1 flex justify-end">
                      <ReadAloudButton
                        text={m.content}
                        testId={`lemonbot-floater-read-aloud-${i}`}
                        inline
                        cta="Listen to feedback"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-yellow-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                  LemonBot is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Quick action chips */}
          <div className="border-t border-yellow-100 bg-yellow-50/60 px-2 py-2 flex-shrink-0">
            <div className="flex gap-1.5 overflow-x-auto pb-1 scroll-smooth">
              {QUICK_ACTIONS.map((action) => {
                const Icon = action.icon;
                const isActive = intentInFlight === action.intent;
                return (
                  <button
                    key={action.intent}
                    onClick={() => handleQuickAction(action)}
                    disabled={loading}
                    data-testid={`lemonbot-quick-${action.intent}`}
                    title={action.label}
                    className={`flex items-center gap-1.5 flex-shrink-0 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${
                      isActive
                        ? "bg-amber-500 text-white border-amber-500"
                        : "bg-white text-amber-800 border-amber-300 hover:bg-amber-50 disabled:opacity-50"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {action.short}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Accessibility learning support shortcuts */}
          <LemonBotHelpButtons onPickPrompt={(p) => { setInput(p); inputRef.current?.focus(); }} />

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            className="flex items-end gap-2 px-3 py-2 border-t border-yellow-100 bg-white flex-shrink-0"
          >
            <input
              id="lemonbot-floater-input-field"
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask LemonBot anything…"
              disabled={loading}
              data-testid="lemonbot-floater-input"
              className="flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
              autoComplete="off"
            />
            <MicButton
              inputId="lemonbot-floater-input-field"
              testId="lemonbot-floater-mic-btn"
              size="sm"
              onTranscript={(t) => {
                setInput((v) => (v ? v + " " : "") + t.trim());
                inputRef.current?.focus();
              }}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="lemonbot-floater-send-btn"
              aria-label="Send message"
              className="bg-gradient-to-br from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white p-2 rounded-full transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default LemonBotFloater;
